import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cnrcDesktopLicensesApi } from '../services/api';
import { CnrcDesktopLicense } from '../types';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import './Pages.css';

const CnrcDesktopLicenses: React.FC = () => {
  const [items, setItems] = useState<CnrcDesktopLicense[]>([]);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(100);
  const [generatedKeys, setGeneratedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { hasPermission } = useAuth();
  const { showToast, showConfirm } = useToast();

  const canCreate = hasPermission('licenses', 'create');
  const canRelease = hasPermission('licenses', 'delete');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cnrcDesktopLicensesApi.getAll(status);
      const data = response.data?.items || response.data?.data || response.data || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erreur chargement licences CNRC';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [status, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return items;
    }

    return items.filter((item) => {
      const text = [
        item.license_key,
        item.machine_code,
        item.pc_name,
        item.windows_user,
        item.machine_hash,
        item.ip_address,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return text.includes(needle);
    });
  }, [items, search]);

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copie`, 'success');
    } catch {
      showToast('Copie impossible depuis le navigateur', 'warning');
    }
  };

  const handleGenerate = async () => {
    const safeCount = Number(count);
    if (!Number.isFinite(safeCount) || safeCount <= 0 || safeCount > 10000) {
      showToast('Nombre entre 1 et 10000 requis', 'error');
      return;
    }

    try {
      setGenerating(true);
      const response = await cnrcDesktopLicensesApi.generate(safeCount);
      const keys = response.data?.keys || [];
      setGeneratedKeys(Array.isArray(keys) ? keys : []);
      showToast(`${response.data?.count || safeCount} cles CNRC generees`, 'success');
      await loadData();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Generation impossible';
      showToast(message, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleRelease = (item: CnrcDesktopLicense) => {
    showConfirm(
      `Liberer la cle ${item.license_key} de ce poste ?`,
      async () => {
        try {
          await cnrcDesktopLicensesApi.release(item.id);
          showToast('Licence CNRC liberee', 'success');
          await loadData();
        } catch (err: any) {
          const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Liberation impossible';
          showToast(message, 'error');
        }
      },
      'Liberer la licence'
    );
  };

  const formatDate = (value?: string) => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Licences CNRC Desktop</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadData} disabled={loading}>
            Actualiser
          </button>
        </div>
      </div>

      {canCreate && (
        <div className="table-container cnrc-admin-panel">
          <div className="cnrc-generate-row">
            <div>
              <h2>Generateur</h2>
              <p>Les cles creees restent libres jusqu'a activation depuis un poste client.</p>
            </div>
            <div className="cnrc-generate-actions">
              <input
                className="search-input cnrc-count-input"
                type="number"
                min="1"
                max="10000"
                value={count}
                onChange={(event) => setCount(Number(event.target.value))}
              />
              <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
                {generating ? 'Generation...' : 'Generer'}
              </button>
            </div>
          </div>

          {generatedKeys.length > 0 && (
            <div className="cnrc-generated-box">
              <div className="cnrc-generated-header">
                <strong>Dernieres cles generees</strong>
                <button
                  className="btn btn-secondary"
                  onClick={() => copyText(generatedKeys.join('\n'), 'Liste des cles')}
                >
                  Copier la liste
                </button>
              </div>
              <textarea readOnly value={generatedKeys.join('\n')} />
            </div>
          )}
        </div>
      )}

      <div className="filters cnrc-license-filters">
        <input
          className="search-input"
          placeholder="Rechercher cle, poste, utilisateur, machine..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">Toutes</option>
          <option value="free">Libres</option>
          <option value="used">Utilisees</option>
        </select>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Cle</th>
              <th>Etat</th>
              <th>Poste</th>
              <th>Utilisateur</th>
              <th>Machine</th>
              <th>Dernier controle</th>
              <th>Creation</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="empty">Chargement...</td>
              </tr>
            )}

            {!loading && filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} className="empty">Aucune licence CNRC a afficher.</td>
              </tr>
            )}

            {!loading && filteredItems.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>
                  <button
                    className="license-key cnrc-copy-key"
                    title="Copier la cle"
                    onClick={() => copyText(item.license_key, 'Cle')}
                  >
                    {item.license_key}
                  </button>
                </td>
                <td>
                  <span className={`status-badge ${item.status === 'used' ? 'active' : 'inactive'}`}>
                    {item.status === 'used' ? 'Utilisee' : 'Libre'}
                  </span>
                </td>
                <td>{item.pc_name || '-'}</td>
                <td>{item.windows_user || '-'}</td>
                <td>
                  <span className="license-key">{item.machine_code || '-'}</span>
                </td>
                <td>{formatDate(item.last_check_at)}</td>
                <td>{formatDate(item.created_at)}</td>
                <td>
                  {item.status === 'used' && canRelease ? (
                    <button className="btn btn-secondary" onClick={() => handleRelease(item)}>
                      Liberer
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CnrcDesktopLicenses;
